import React, { useState, useEffect, useRef } from 'react';
import { 
  Plus, 
  Mail, 
  Send, 
  Calendar, 
  Users, 
  TrendingUp, 
  Eye, 
  MousePointer, 
  UserMinus,
  Clock,
  CheckCircle,
  AlertCircle,
  Pause,
  Play,
  Copy,
  Edit3,
  Trash2,
  Filter,
  Search,
  MoreHorizontal,
  Settings,
  Target,
  BarChart3,
  Zap,
  TestTube,
  ArrowRight,
  ChevronDown,
  X,
  Loader2,
  FileText,
  Image,
  Link,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { useAuth } from '../../hooks/useAuth';
import { useSupabase } from '../../contexts/SupabaseContext';
import { useBusinessId } from '../../hooks/useBusinessId';

// Types
interface EmailCampaign {
  id: string;
  name: string;
  subject: string;
  from_email: string;
  from_name: string;
  html_content: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'paused' | 'failed';
  campaign_type: 'standard' | 'ab_test' | 'automated';
  is_ab_test: boolean;
  recipient_count: number;
  sent_count: number;
  delivered_count: number;
  opened_count: number;
  clicked_count: number;
  scheduled_at?: string;
  sent_at?: string;
  created_at: string;
  updated_at: string;
}

interface EmailMarketingSettings {
  id: string;
  business_id: string;
  verified_emails: string[];
  monthly_email_limit: number;
  emails_sent_this_month: number;
  is_enabled: boolean;
}







// Campaign Builder Form Component
function CampaignBuilderForm({ 
  onClose, 
  onSave, 
  editingCampaign, 
  selectedBusiness,
  businessId,
  supabase
}: { 
  onClose: () => void;
  onSave: (campaign: any) => void;
  editingCampaign: EmailCampaign | null;
  selectedBusiness: any;
  businessId: string;
  supabase: any;
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [useTemplate, setUseTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [emailTemplates, setEmailTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  const [formData, setFormData] = useState({
    name: editingCampaign?.name || '',
    subject: editingCampaign?.subject || '',
    from_email: editingCampaign?.from_email || 'test@business.com',
    from_name: editingCampaign?.from_name || selectedBusiness?.name || 'Test Business',
    html_content: editingCampaign?.html_content || '<p>Enter your email content here...</p>'
  });
  const [saving, setSaving] = useState(false);

  // Load email templates when component mounts or when useTemplate is enabled
  useEffect(() => {
    console.log('[TEMPLATES] useEffect triggered:', { useTemplate, emailTemplatesLength: emailTemplates.length, businessId });
    if (useTemplate && emailTemplates.length === 0) {
      console.log('[TEMPLATES] Calling loadEmailTemplates...');
      loadEmailTemplates();
    }
  }, [useTemplate]);

  const loadEmailTemplates = async () => {
    console.log('[TEMPLATES] loadEmailTemplates called:', { supabase: !!supabase, businessId });
    
    if (!supabase || !businessId) {
      console.log('[TEMPLATES] Early return - missing supabase or businessId');
      return;
    }
    
    setLoadingTemplates(true);
    console.log('[TEMPLATES] Starting template query...');
    
    try {
      console.log('[TEMPLATES] About to run query with businessId:', businessId);
      
      // First try a simple query to see what columns exist
      const { data: allTemplates, error: testError } = await supabase
        .from('email_templates')
        .select('*')
        .eq('business_id', businessId)
        .limit(1);
      
      console.log('[TEMPLATES] Test query result:', { allTemplates, testError });
      
      // Now try the specific query with correct column names
      const { data: templates, error } = await supabase
        .from('email_templates')
        .select('id, name, subject, content, template_type')
        .eq('business_id', businessId)
        .order('name');

      console.log('[TEMPLATES] Raw query result:', { 
        templates, 
        error, 
        templatesCount: templates?.length,
        errorMessage: error?.message,
        errorDetails: error?.details,
        errorHint: error?.hint
      });

      if (error) {
        console.error('[TEMPLATES] Database error details:', error);
        throw error;
      }
      
      setEmailTemplates(templates || []);
      console.log('[TEMPLATES] Templates set in state:', templates?.length || 0);
    } catch (error) {
      console.error('[TEMPLATES] Error loading email templates:', error);
      setEmailTemplates([]);
    } finally {
      setLoadingTemplates(false);
      console.log('[TEMPLATES] Loading finished');
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    console.log('[TEMPLATES] Template selected:', { templateId, availableTemplates: emailTemplates.length });
    
    const template = emailTemplates.find(t => t.id === templateId);
    console.log('[TEMPLATES] Found template:', template);
    
    if (template) {
      const newFormData = {
        html_content: template.content, // Use 'content' column from actual table
        subject: template.subject || template.name // Use template subject or name as fallback
      };
      console.log('[TEMPLATES] Setting form data:', newFormData);
      
      setFormData(prev => ({
        ...prev,
        ...newFormData
      }));
    }
    setSelectedTemplate(templateId);
  };

  const handleNextStep = () => {
    setCurrentStep(2);
  };

  const handlePreviousStep = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const campaignData = {
        business_id: selectedBusiness?.id,
        name: formData.name,
        subject: formData.subject,
        from_email: formData.from_email,
        from_name: formData.from_name,
        html_content: formData.html_content,
        status: 'draft',
        campaign_type: 'standard',
        is_ab_test: false,
        recipient_count: 0,
        sent_count: 0,
        delivered_count: 0,
        opened_count: 0,
        clicked_count: 0
      };

      await onSave(campaignData);
      onClose();
    } catch (error) {
      console.error('Error saving campaign:', error);
      alert('Failed to save campaign. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Step Progress Indicator */}
      <div className="flex items-center justify-center space-x-4 mb-8">
        <div className={`flex items-center space-x-2 ${currentStep === 1 ? 'text-blue-600' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            currentStep === 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
          }`}>
            1
          </div>
          <span className="font-medium">Campaign Details</span>
        </div>
        <div className="w-12 h-px bg-gray-300"></div>
        <div className={`flex items-center space-x-2 ${currentStep === 2 ? 'text-blue-600' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            currentStep === 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
          }`}>
            2
          </div>
          <span className="font-medium">Recipients & Settings</span>
        </div>
      </div>

      {/* Test Mode Warning */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <AlertCircle className="h-4 w-4 text-orange-600" />
          <span className="text-sm font-medium text-orange-900">Test Mode</span>
        </div>
        <p className="text-sm text-orange-700 mt-1">
          This is a basic campaign builder for testing. SendGrid integration will be added later.
        </p>
      </div>

      {/* Step 1: Campaign Details */}
      {currentStep === 1 && (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Campaign Title
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Welcome New Customers"
            />
          </div>

          {/* Template Selection */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="useTemplate"
                checked={useTemplate}
                onChange={(e) => {
                  console.log('[TEMPLATES] Checkbox changed:', e.target.checked);
                  setUseTemplate(e.target.checked);
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="useTemplate" className="text-sm font-medium text-gray-700">
                Use an email template
              </label>
            </div>

            {useTemplate && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Template
                </label>
                {loadingTemplates ? (
                  <div className="flex items-center space-x-2 text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading templates...</span>
                  </div>
                ) : (
                  <select
                    value={selectedTemplate}
                    onChange={(e) => {
                      console.log('[TEMPLATES] Dropdown changed:', e.target.value);
                      handleTemplateSelect(e.target.value);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a template...</option>
                    {emailTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.template_type})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          {/* Email Subject - Only show if not using template */}
          {!useTemplate && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Subject
              </label>
              <input
                type="text"
                required
                value={formData.subject}
                onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Welcome to our family! 🎉"
              />
            </div>
          )}

          {/* Email Content - Only show if not using template */}
          {!useTemplate && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Content
              </label>
              <textarea
                required
                rows={8}
                value={formData.html_content}
                onChange={(e) => setFormData(prev => ({ ...prev, html_content: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your email content here..."
              />
              <p className="text-sm text-gray-500 mt-1">
                You can use HTML tags for formatting. Rich text editor coming soon!
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleNextStep}
              disabled={
                !formData.name || 
                (useTemplate ? !selectedTemplate : (!formData.subject || !formData.html_content))
              }
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
            >
              Next Step
              <ArrowRight className="h-4 w-4 ml-2 inline" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Recipients & Settings (Placeholder) */}
      {currentStep === 2 && (
        <div className="space-y-6">
          <div className="text-center py-12">
            <div className="h-16 w-16 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Step 2 Coming Soon</h3>
            <p className="text-gray-600">
              This step will include recipient selection and campaign settings.
            </p>
          </div>

          <div className="flex justify-between pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handlePreviousStep}
              className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowRight className="h-4 w-4 mr-2 inline rotate-180" />
              Previous Step
            </button>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 inline animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Campaign'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Campaigns() {
  const { selectedBusiness } = useBusiness();
  const { user } = useAuth();
  const supabase = useSupabase();
  const businessId = useBusinessId();
  
  // State
  const [activeTab, setActiveTab] = useState<'overview' | 'campaigns' | 'settings'>('overview');
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [settings, setSettings] = useState<EmailMarketingSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCampaignBuilder, setShowCampaignBuilder] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<EmailCampaign | null>(null);
  const [showEmailSetup, setShowEmailSetup] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [verifyingEmail, setVerifyingEmail] = useState(false);

  // Load data
  useEffect(() => {
    if (businessId) {
      loadEmailSettings();
      loadCampaigns();
    }
  }, [businessId]);

  const loadEmailSettings = async () => {
    if (!businessId) return;
    
    try {
      setLoading(true);
      
      // Check if settings exist
      const { data: existingSettings } = await supabase
        .from('email_marketing_settings')
        .select('*')
        .eq('business_id', businessId)
        .single();

      if (existingSettings) {
        setSettings(existingSettings);
      } else {
        // Create default settings
        const { data: newSettings, error } = await supabase
          .from('email_marketing_settings')
          .insert({
            business_id: businessId,
            verified_emails: [],
            monthly_email_limit: 5000,
            emails_sent_this_month: 0,
            is_enabled: true
          })
          .select()
          .single();

        if (error) throw error;
        setSettings(newSettings);
      }
    } catch (error) {
      console.error('Error loading email settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    if (!businessId) return;
    
    try {
      const { data: campaignData, error } = await supabase
        .from('email_campaigns')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setCampaigns(campaignData || []);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      // Keep campaigns as empty array on error
      setCampaigns([]);
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail.trim() || !businessId) return;
    
    setVerifyingEmail(true);
    
    try {
      console.log('🔗 Starting SendGrid email verification for:', newEmail);
      
      // Call the existing verify-email function
      const { data: verificationResult, error: verificationError } = await supabase.functions.invoke('verify-email', {
        body: {
          email: newEmail.trim(),
          businessId: businessId,
          userId: user?.id,
          frontendUrl: window.location.origin
        }
      });

      if (verificationError) {
        throw new Error(verificationError.message || 'Failed to send verification email');
      }

      if (!verificationResult?.success) {
        throw new Error('Email verification request failed');
      }

      console.log('✅ Verification email sent successfully');
      
      // Show success message with instructions
      alert(`Verification email sent to ${newEmail}! Please check your inbox and click the verification link to complete the process. Once verified, you'll be able to send campaigns from this address.`);
      
      setNewEmail('');
      setShowEmailSetup(false);
      
      // Reload settings to check for verification status
      setTimeout(() => {
        loadEmailSettings();
      }, 1000);
      
    } catch (error) {
      console.error('Error verifying email:', error);
      alert(`Failed to send verification email: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`);
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleCreateCampaign = () => {
    setEditingCampaign(null);
    setShowCampaignBuilder(true);
  };

  const handleEditCampaign = (campaign: EmailCampaign) => {
    setEditingCampaign(campaign);
    setShowCampaignBuilder(true);
  };

  const handleSaveCampaign = async (campaignData: any) => {
    if (!businessId) return;

    try {
      if (editingCampaign?.id) {
        // Update existing campaign
        const { data: updatedCampaign, error } = await supabase
          .from('email_campaigns')
          .update(campaignData)
          .eq('id', editingCampaign.id)
          .eq('business_id', businessId)
          .select()
          .single();

        if (error) throw error;

        // Update local state
        setCampaigns(prev => prev.map(c => c.id === editingCampaign.id ? updatedCampaign : c));
        alert('Campaign updated successfully!');
      } else {
        // Create new campaign
        const { data: newCampaign, error } = await supabase
          .from('email_campaigns')
          .insert(campaignData)
          .select()
          .single();

        if (error) throw error;

        // Add to local state
        setCampaigns(prev => [newCampaign, ...prev]);
        alert('Campaign created successfully! (Test Mode - No actual email sent)');
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      throw error; // Re-throw so the form can handle it
    }
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      const { error } = await supabase
        .from('email_campaigns')
        .delete()
        .eq('id', campaignId)
        .eq('business_id', businessId);

      if (error) throw error;

      // Remove from local state
      setCampaigns(prev => prev.filter(c => c.id !== campaignId));
      alert('Campaign deleted successfully!');
    } catch (error) {
      console.error('Error deleting campaign:', error);
      alert('Failed to delete campaign. Please try again.');
    }
  };

  const handleDuplicateCampaign = async (campaign: EmailCampaign) => {
    if (!businessId) return;
    
    try {
      const duplicatedData = {
        business_id: businessId,
        name: `${campaign.name} (Copy)`,
        subject: campaign.subject,
        from_email: campaign.from_email,
        from_name: campaign.from_name,
        html_content: campaign.html_content,
        status: 'draft',
        campaign_type: campaign.campaign_type,
        is_ab_test: false, // Reset A/B test for duplicates
        recipient_count: 0,
        sent_count: 0,
        delivered_count: 0,
        opened_count: 0,
        clicked_count: 0
      };

      const { data: newCampaign, error } = await supabase
        .from('email_campaigns')
        .insert(duplicatedData)
        .select()
        .single();

      if (error) throw error;

      // Add to local state
      setCampaigns(prev => [newCampaign, ...prev]);
      alert('Campaign duplicated successfully!');
    } catch (error) {
      console.error('Error duplicating campaign:', error);
      alert('Failed to duplicate campaign. Please try again.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'bg-green-100 text-green-800';
      case 'sending': return 'bg-blue-100 text-blue-800';
      case 'scheduled': return 'bg-yellow-100 text-yellow-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'paused': return 'bg-orange-100 text-orange-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent': return <CheckCircle className="h-4 w-4" />;
      case 'sending': return <Loader2 className="h-4 w-4 animate-spin" />;
      case 'scheduled': return <Clock className="h-4 w-4" />;
      case 'draft': return <FileText className="h-4 w-4" />;
      case 'paused': return <Pause className="h-4 w-4" />;
      case 'failed': return <AlertCircle className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         campaign.subject.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const calculateOpenRate = (opened: number, delivered: number) => {
    return delivered > 0 ? ((opened / delivered) * 100).toFixed(1) : '0.0';
  };

  const calculateClickRate = (clicked: number, delivered: number) => {
    return delivered > 0 ? ((clicked / delivered) * 100).toFixed(1) : '0.0';
  };

  // Overview Tab
  const OverviewTab = () => (
    <div className="space-y-6 p-6">
      {/* Test Mode Banner */}
      {(!settings?.verified_emails || settings.verified_emails.length === 0) && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <div className="flex items-center space-x-3">
            <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center">
              <AlertCircle className="h-4 w-4 text-orange-600" />
            </div>
            <div>
              <h4 className="font-medium text-orange-900">Test Mode Active</h4>
              <p className="text-sm text-orange-700">
                SendGrid integration not configured. You can create and manage campaigns for testing purposes.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Email Setup Status */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <Mail className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Email Setup</h3>
              <p className="text-sm text-gray-600">Configure your sending email addresses</p>
            </div>
          </div>
          <button
            onClick={() => setShowEmailSetup(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2 inline" />
            Add Email
          </button>
        </div>

        {settings?.verified_emails && settings.verified_emails.length > 0 ? (
          <div className="space-y-2">
            {settings.verified_emails.map((email, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-gray-900">{email}</span>
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Verified</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <Mail className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Email Addresses Added</h4>
            <p className="text-gray-600 mb-4">Add and verify your email address to start sending campaigns</p>
            <button
              onClick={() => setShowEmailSetup(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add Your First Email
            </button>
          </div>
        )}
      </div>

      {/* Usage Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
              <Send className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">This Month</h3>
              <p className="text-sm text-gray-600">Emails sent</p>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold text-gray-900">
                {settings?.emails_sent_this_month?.toLocaleString() || '0'}
              </span>
              <span className="text-sm text-gray-500">
                / {settings?.monthly_email_limit?.toLocaleString() || '5,000'}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: `${Math.min(((settings?.emails_sent_this_month || 0) / (settings?.monthly_email_limit || 5000)) * 100, 100)}%` 
                }}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Avg. Open Rate</h3>
              <p className="text-sm text-gray-600">Last 30 days</p>
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900">24.3%</div>
          <div className="text-sm text-green-600 mt-1">+2.1% from last month</div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
              <MousePointer className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Avg. Click Rate</h3>
              <p className="text-sm text-gray-600">Last 30 days</p>
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900">3.8%</div>
          <div className="text-sm text-green-600 mt-1">+0.5% from last month</div>
        </div>
      </div>

      {/* Recent Campaigns */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Recent Campaigns</h3>
          <button
            onClick={() => setActiveTab('campaigns')}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            View All
          </button>
        </div>
        
        {campaigns.slice(0, 3).map((campaign) => (
          <div key={campaign.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg mb-3 last:mb-0">
            <div className="flex items-center space-x-4">
              <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                {getStatusIcon(campaign.status)}
              </div>
              <div>
                <h4 className="font-medium text-gray-900">{campaign.name}</h4>
                <p className="text-sm text-gray-600">{campaign.subject}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">
                  {campaign.delivered_count > 0 ? `${calculateOpenRate(campaign.opened_count, campaign.delivered_count)}%` : '-'}
                </div>
                <div className="text-xs text-gray-500">Open Rate</div>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(campaign.status)}`}>
                {campaign.status}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Campaigns Tab
  const CampaignsTab = () => (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Email Campaigns</h2>
          <p className="text-gray-600">Create and manage your email marketing campaigns</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {(!settings?.verified_emails || settings.verified_emails.length === 0) && (
            <div className="px-3 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
              Test Mode - No SendGrid Integration
            </div>
          )}
          <button
            onClick={handleCreateCampaign}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
          >
            <Plus className="h-5 w-5 mr-2 inline" />
            Create Campaign
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Status</option>
          <option value="draft">Draft</option>
          <option value="scheduled">Scheduled</option>
          <option value="sending">Sending</option>
          <option value="sent">Sent</option>
          <option value="paused">Paused</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {/* Campaigns List */}
      {filteredCampaigns.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="h-16 w-16 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Mail className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Campaigns Found</h3>
          <p className="text-gray-600 mb-6">
            {searchQuery || statusFilter !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Create your first email campaign to get started'
            }
          </p>
          {!searchQuery && statusFilter === 'all' && (
            <div className="flex flex-col items-center gap-2">
              {(!settings?.verified_emails || settings.verified_emails.length === 0) && (
                <div className="px-3 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                  Test Mode - No SendGrid Integration
                </div>
              )}
              <button
                onClick={handleCreateCampaign}
                className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
              >
                <Plus className="h-5 w-5 mr-2 inline" />
                Create Your First Campaign
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredCampaigns.map((campaign) => (
            <div key={campaign.id} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{campaign.name}</h3>
                    {campaign.is_ab_test && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                        <TestTube className="h-3 w-3 mr-1 inline" />
                        A/B Test
                      </span>
                    )}
                    <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center space-x-1 ${getStatusColor(campaign.status)}`}>
                      {getStatusIcon(campaign.status)}
                      <span className="capitalize">{campaign.status}</span>
                    </span>
                  </div>
                  <p className="text-gray-600 mb-2">{campaign.subject}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>From: {campaign.from_email}</span>
                    <span>•</span>
                    <span>Recipients: {campaign.recipient_count.toLocaleString()}</span>
                    {campaign.scheduled_at && (
                      <>
                        <span>•</span>
                        <span>Scheduled: {new Date(campaign.scheduled_at).toLocaleDateString()}</span>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEditCampaign(campaign)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDuplicateCampaign(campaign)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteCampaign(campaign.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Campaign Stats */}
              {campaign.status === 'sent' && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{campaign.delivered_count.toLocaleString()}</div>
                    <div className="text-sm text-gray-500">Delivered</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {calculateOpenRate(campaign.opened_count, campaign.delivered_count)}%
                    </div>
                    <div className="text-sm text-gray-500">Open Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {calculateClickRate(campaign.clicked_count, campaign.delivered_count)}%
                    </div>
                    <div className="text-sm text-gray-500">Click Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{campaign.clicked_count}</div>
                    <div className="text-sm text-gray-500">Clicks</div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );



  // Settings Tab
  const SettingsTab = () => (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Email Marketing Settings</h2>
        <p className="text-gray-600">Configure your email marketing preferences</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Email Limit</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <div className="font-medium text-gray-900">Current Plan</div>
              <div className="text-sm text-gray-600">5,000 emails per month</div>
            </div>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              Upgrade Plan
            </button>
          </div>
          <div className="text-sm text-gray-600">
            You've used {settings?.emails_sent_this_month || 0} of {settings?.monthly_email_limit || 5000} emails this month.
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Verified Email Addresses</h3>
        <div className="space-y-3">
          {settings?.verified_emails && settings.verified_emails.length > 0 ? (
            settings.verified_emails.map((email, index) => (
              <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-gray-900">{email}</span>
                </div>
                <button className="text-red-600 hover:text-red-700 text-sm">
                  Remove
                </button>
              </div>
            ))
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-600">No verified email addresses</p>
            </div>
          )}
          <button
            onClick={() => setShowEmailSetup(true)}
            className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
          >
            <Plus className="h-5 w-5 mr-2 inline" />
            Add Email Address
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-100">
      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 bg-white px-6 py-4">
        <nav className="flex space-x-8">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'campaigns', label: 'Campaigns', icon: Mail },
            { id: 'settings', label: 'Settings', icon: Settings }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'campaigns' && <CampaignsTab />}
        {activeTab === 'settings' && <SettingsTab />}
      </div>

      {/* Email Setup Modal */}
      {showEmailSetup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Email Address</h3>
              <button
                onClick={() => setShowEmailSetup(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="hello@yourbusiness.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-800">
                  We'll send a verification email to this address. Click the link in the email to verify and start sending campaigns.
                </p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowEmailSetup(false)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddEmail}
                  disabled={!newEmail.trim() || verifyingEmail}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
                >
                  {verifyingEmail ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 inline animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Add & Verify'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Builder Modal - Placeholder */}
      {showCampaignBuilder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  {editingCampaign?.id ? 'Edit Campaign' : 'Create New Campaign'}
                </h3>
                <button
                  onClick={() => setShowCampaignBuilder(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <CampaignBuilderForm 
                onClose={() => setShowCampaignBuilder(false)}
                onSave={handleSaveCampaign}
                editingCampaign={editingCampaign}
                selectedBusiness={selectedBusiness}
                businessId={businessId}
                supabase={supabase}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
